import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Star, Award } from 'lucide-react';

// --- IMAGE IMPORTS ---
// (Make sure paths are correct based on your folder structure)
import img1 from '../assest/img1 (2).png';
import img2 from '../assest/img2 (2).png';
import img3 from '../assest/img3.jpeg';
import img4 from '../assest/img4.jpeg';
import img5 from '../assest/img1 (2).png';
import img6 from '../assest/img2 (2).png';

const images = [img1, img2, img3, img4, img5, img6];

// --- ANIMATION VARIANTS ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.9 },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { type: "spring", stiffness: 60, damping: 12 }
  },
};

const PastGlimpses: React.FC = () => {
  return (
    <section className="relative min-h-screen bg-gradient-to-b from-[#FFF6EC] to-white py-20 px-6 overflow-hidden">
      
      {/* --- BACKGROUND DECORATION (Subtle Patterns) --- */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-400/20 rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 right-10 w-32 h-32 bg-[#9e1c22]/10 rounded-full blur-3xl"></div>
          {/* Floating Icons */}
          <Star className="absolute top-1/4 left-5 text-yellow-400/40 w-8 h-8 animate-pulse" />
          <Award className="absolute bottom-1/3 right-10 text-[#9e1c22]/20 w-12 h-12 rotate-12" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* --- HEADER --- */}
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#9e1c22]/10 border border-[#9e1c22]/20 rounded-full px-5 py-2 mb-4"
          >
            <Camera className="w-4 h-4 text-[#9e1c22]" />
            <span className="text-xs font-bold tracking-widest text-[#9e1c22] uppercase">
              Captured Moments
            </span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-black text-[#2d2d2d] tracking-tight"
          >
            GLIMPSES OF <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9e1c22] to-[#d92830]">SUCCESS</span>
          </motion.h2>
          
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto font-medium text-lg">
            Witness the joy, pride, and celebration of excellence at the PWNSAT Mega Felicitation Ceremony!
          </p>
        </div>

        {/* --- GALLERY GRID --- */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {images.map((src, index) => (
            <motion.div
              key={index}
              // variants={cardVariants}
              className="group relative cursor-pointer"
            >
              {/* Card Container */}
              <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.1)] border-4 border-white transition-all duration-500 group-hover:shadow-[0_20px_50px_rgba(158,28,34,0.2)] group-hover:-translate-y-2">
                
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={src} 
                    alt={`Event Moment ${index + 1}`} 
                    className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110" 
                  />
                </div>

                {/* Overlay (Hidden by default, shown on hover) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <span className="block text-yellow-400 text-xs font-bold uppercase tracking-wider mb-1">
                      PWNSAT 2025
                    </span>
                    <h3 className="text-white text-xl font-bold">
                      Celebrating Toppers
                    </h3>
                  </div>
                </div>

                {/* Decorative Corner Border (Gold) */}
                <div className="absolute top-0 left-0 w-full h-full border-2 border-transparent group-hover:border-[#FFD700]/50 rounded-xl transition-colors duration-300 pointer-events-none m-1"></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* --- BOTTOM CTA --- */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16"
        >
          {/* <button className="px-8 py-3 bg-white border-2 border-[#9e1c22] text-[#9e1c22] font-bold rounded-full hover:bg-[#9e1c22] hover:text-white transition-all duration-300 shadow-md hover:shadow-xl active:scale-95 uppercase text-sm tracking-wide">
            View Full Gallery
          </button> */}
        </motion.div>

      </div>
    </section>
  );
};

export default PastGlimpses;